/**
 * ทะเบียนรอบการขาย (Firestore `stores/{storeId}/appState/businessSession`)
 *
 * ปัญหาที่แก้: เครื่องที่ไม่ได้ปิด browser ข้ามรอบ (เช่น cashier 2 พับจอ notebook ไว้ข้ามคืน)
 * ยังถือ `jobsStore.openShop` ของรอบที่ปิดไปแล้ว พอร้านเปิดรอบใหม่ เครื่องนั้นก็ยังขายต่อได้
 * แล้วใบเสร็จไปลง businessDate ของรอบเก่า
 *
 * กติกา:
 *   - เปิดร้าน  = append ของ OPEN ลง array อย่างเดียว (รอบเดียวกันเปิดซ้ำได้ เช่นเปลี่ยนเครื่องหลังเที่ยงคืน)
 *   - Main Cashier ปิดร้าน = ลบทั้ง doc
 *   - ไม่มี doc = ขายได้เสมอ (ช่วงเปลี่ยนผ่าน / ร้านที่เปิดมาจากเวอร์ชันเก่า)
 *   - jobId ของ OPEN เครื่องเราอยู่ใน doc = ขายได้
 *   - jobId ไม่อยู่ใน doc "และ" doc มี OPEN ที่ใหม่กว่าของเรา = เครื่องเราค้างรอบเก่า ห้ามขาย
 *
 * เงื่อนไข "ใหม่กว่า" กันเคส doc ค้างของเก่า (ลบตอนปิดร้านไม่สำเร็จ หรือเครื่องเวอร์ชันเก่า
 * เป็นคนปิด/เปิดร้าน) ถ้าไม่มีเงื่อนไขนี้ เครื่องทั้งร้านที่อยู่รอบปัจจุบันจะจ่ายเงินไม่ได้
 * หลักคือ ไม่แน่ใจ = ขายได้ บล็อกเฉพาะตอนมีหลักฐานว่ามีรอบใหม่กว่าเกิดขึ้นแล้วจริง
 *
 * ไฟล์นี้ต้องเป็น pure function ไม่ import อะไรเลย เพื่อให้ scripts/test-business-session.mjs เทสต์ได้ตรง ๆ
 */

export interface BusinessSessionDoc {
  businessDates?: string[];
  openJobIds?: string[];
  openedAt?: string[];
}

export interface OpenJobLike {
  jobId?: string;
  timestamp?: string | number;
  businessDate?: string;
}

export interface BusinessSessionAppend {
  businessDates: string[];
  openJobIds: string[];
  openedAt: string[];
}

function toEpoch(value: unknown): number {
  if (typeof value === "number") return value;
  if (typeof value === "string" && value !== "") return Date.parse(value);
  return NaN;
}

function asStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v) => typeof v === "string" && v !== "") : [];
}

/**
 * ค่าที่ต้อง append ลง doc ตอนกดเปิดร้าน
 *
 * ใส่ OPEN เดิมของเครื่อง (ถ้ามี) ไปด้วย เผื่อรอบนี้เปิดมาตั้งแต่ก่อน deploy แล้วยังไม่มี doc
 * ไม่งั้นทุกเครื่องที่ยึด OPEN ตัวแรกจะไม่มี jobId ของตัวเองอยู่ใน doc
 */
export function buildBusinessSessionAppend(
  newOpenJob: OpenJobLike,
  currentOpenShop?: OpenJobLike | null
): BusinessSessionAppend {
  const jobs = [currentOpenShop, newOpenJob].filter(Boolean) as OpenJobLike[];
  const unique = (values: unknown[]) => Array.from(new Set(asStringArray(values)));

  return {
    businessDates: unique(jobs.map((job) => job.businessDate)),
    openJobIds: unique(jobs.map((job) => job.jobId)),
    openedAt: unique(
      jobs.map((job) => {
        const epoch = toEpoch(job.timestamp);
        return isNaN(epoch) ? undefined : new Date(epoch).toISOString();
      })
    ),
  };
}

/**
 * เวลาเปิดของรอบปัจจุบันบน cloud ที่ใหม่กว่าเครื่องเรา (ตัวแรกหลังจาก OPEN ของเรา)
 * คืน null ถ้าไม่มี = เครื่องเราไม่ได้ค้างรอบเก่า
 */
export function findNewerOpenedAt(
  openShop: OpenJobLike | null | undefined,
  session: BusinessSessionDoc | null | undefined
): string | null {
  if (!openShop || !session) return null;

  const mine = toEpoch(openShop.timestamp);
  if (isNaN(mine)) return null;

  const newer = asStringArray(session.openedAt)
    .map((value) => ({ value, epoch: toEpoch(value) }))
    .filter((entry) => !isNaN(entry.epoch) && entry.epoch > mine)
    .sort((a, b) => a.epoch - b.epoch);

  return newer.length > 0 ? newer[0].value : null;
}

export function isBusinessSessionStale(
  openShop: OpenJobLike | null | undefined,
  session: BusinessSessionDoc | null | undefined
): boolean {
  // ไม่มี doc หรือเครื่องยังไม่ได้เปิดร้าน = ไม่มีอะไรให้เทียบ ขายได้
  if (!openShop || !session) return false;

  const openJobIds = asStringArray(session.openJobIds);
  if (openJobIds.length === 0) return false;

  // รอบเดียวกัน (รวมกรณีมีเครื่องอื่นกด Open ซ้ำในรอบนี้)
  if (openShop.jobId && openJobIds.includes(openShop.jobId)) return false;

  // doc เก่ากว่าเรา = doc ค้างจากรอบก่อน ไม่ใช่เครื่องเราค้าง
  return findNewerOpenedAt(openShop, session) !== null;
}
